// ✅ src/pages/Asset/MakeCapacityListing.jsx
// FULL FILE - API + PAGINATION + SEARCH + DELETE + THEME + LOCALSTORAGE HEADERS
//
// Backend mount:
//   app.use("/api/makeListing", makeListing);
//
// Expected endpoints:
//   GET    https://nissi-india.com/api/makeListing/list?page=&pageSize=&search=
//   DELETE https://nissi-india.com/api/makeListing/delete/:id
//   GET    https://nissi-india.com/api/makeListing/getById/:id   (optional for edit page)



import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/makeListing";

const MakeCapacityListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();

  // ✅ localStorage data
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers for backend
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // UI state
  const [showFilters, setShowFilters] = useState(true);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");

  // grid state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const canPrev = page > 1;
  const canNext = page < totalPages;

  // ✅ Status counts for Results Summary panel (current page only)
  const statusCounts = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        const status = r.status ?? r.Status ?? r.fld_status ?? "Active";
        if (String(status).toLowerCase() === "active") acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { active: 0, inactive: 0 }
    );
  }, [rows]);

  const fetchList = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextPageSize = opts.pageSize ?? pageSize;
    const nextSearch = opts.search ?? search;

    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          page: nextPage,
          pageSize: nextPageSize,
          search: nextSearch || "",
        },
        headers: commonHeaders,
      });

      console.log("res:", res.data?.data);

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load list");
        setRows([]);
        setTotalCount(0);
        setTotalPages(1);
        return;
      }

      setRows(res.data.data || []);
      setTotalCount(res.data.totalCount ?? 0);
      setTotalPages(res.data.totalPages ?? 1);

      // If backend returns actual page/pageSize, trust it
      setPage(res.data.page ?? nextPage);
      setPageSize(res.data.pageSize ?? nextPageSize);
    } catch (err) {
      console.error("make listing fetch error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to load Make/Capacity listing"
      );
      setRows([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ page: 1, pageSize: 10, search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const handleSearch = () => {
    const val = (searchDraft || "").trim();
    setSearch(val);
    setPage(1);
    fetchList({ page: 1, search: val });
  };

  const handleClear = () => {
    setSearchDraft("");
    setSearch("");
    setPage(1);
    fetchList({ page: 1, search: "" });
  };

  const handleAddNew = () => {
    navigate("/Asset/MakeMaster");
  };

  const handleEdit = (row) => {
    navigate(`/Asset/MakeMaster?id=${row.fld_makeid ?? row.MakeId ?? row.id}`);
  };

  const handleDelete = async (row) => {
    const id = row.fld_makeid ?? row.MakeId ?? row.id;
    const name = row.makename ?? row.MakeName ?? row.makeName ?? "-";

    if (!id) {
      toast.error("Missing id for delete");
      return;
    }

    const ok = window.confirm(`Delete "${name}"?`);
    if (!ok) return;

    try {
      const res = await axios.delete(`${API_BASE}/delete/${id}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success("Deleted successfully");

      const willBeEmpty = rows.length === 1 && page > 1;
      const targetPage = willBeEmpty ? page - 1 : page;

      setPage(targetPage);
      fetchList({ page: targetPage });
    } catch (err) {
      console.error("make delete error:", err);
      toast.error(err?.response?.data?.message || "Failed to delete");
    }
  };

  const handlePageChange = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    fetchList({ page: p });
  };

  const pageNumbers = useMemo(() => {
    const maxVisible = 10;
    const pages = [];

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) pages.push("...");
    return pages;
  }, [page, totalPages]);

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">MAKE / CAPACITY MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Make / Capacity Listing
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClear}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    Search
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Make Name / Code
                </label>
                <input
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search Make Name / Code"
                  className={`w-full max-w-md px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Make Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Make Code</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Remarks</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Status</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loading ? (
                  <tr>
                    <td colSpan={6} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const id = r.fld_makeid ?? r.MakeId ?? r.id;
                    const makeName =
                      r.fld_MakeName ?? r.fld_makename ?? r.MakeName ?? r.makeName ?? "-";
                    const makeCode =
                      r.fld_MakeCode ?? r.fld_makecode ?? r.MakeCode ?? r.makeCode ?? "-";
                    const remarks =
                      r.fld_Remarks ?? r.Remarks ?? r.fld_remarks ?? "";
                    const status = r.status ?? r.Status ?? r.fld_status ?? "Active";

                    return (
                      <tr
                        key={id ?? idx}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {makeName}
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {makeCode}
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {remarks || "-"}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              String(status).toLowerCase() === "active"
                                ? isDark
                                  ? "bg-green-800 text-green-100"
                                  : "bg-green-100 text-green-800"
                                : isDark
                                ? "bg-red-800 text-red-100"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {status}
                          </span>
                        </td>

                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center gap-3">
                            <button
                              type="button"
                              onClick={() => handleEdit(r)}
                              className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                              title="Edit"
                            >
                              ✏️
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(r)}
                              className="text-red-500 hover:text-red-600 text-lg transition"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
            <div className={`${isDark ? "text-white/80" : "text-gray-700"} text-sm`}>
              Showing <b>{rows.length}</b> of <b>{totalCount}</b> records
              {search ? (
                <span className="ml-2">
                  (Search: <b>{search}</b>)
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  const ps = parseInt(e.target.value, 10);
                  setPageSize(ps);
                  setPage(1);
                  fetchList({ page: 1, pageSize: ps });
                }}
                className={`px-3 py-2 rounded-lg border text-sm outline-none ${
                  isDark
                    ? "bg-white/10 border-white/10 text-white"
                    : "bg-white border-gray-200 text-gray-900"
                }`}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n} className="text-black">
                    {n} / page
                  </option>
                ))}
              </select>

              <button
                onClick={() => canPrev && handlePageChange(page - 1)}
                disabled={!canPrev}
                className={`p-2 rounded-lg border transition-all ${
                  !canPrev
                    ? isDark
                      ? "bg-white/5 border-white/10 text-white/30 cursor-not-allowed"
                      : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                    : isDark
                    ? "bg-white/10 border-white/10 text-white hover:bg-white/15"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
                title="Previous"
              >
                <FaChevronLeft />
              </button>

              <div className="flex gap-1">
                {pageNumbers.map((p, idx) => (
                  <button
                    key={`${p}-${idx}`}
                    onClick={() => p !== "..." && handlePageChange(p)}
                    disabled={p === "..."}
                    className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                      p === page
                        ? "bg-blue-600 text-white border-blue-600"
                        : p === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                        ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <button
                onClick={() => canNext && handlePageChange(page + 1)}
                disabled={!canNext}
                className={`p-2 rounded-lg border transition-all ${
                  !canNext
                    ? isDark
                      ? "bg-white/5 border-white/10 text-white/30 cursor-not-allowed"
                      : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                    : isDark
                    ? "bg-white/10 border-white/10 text-white hover:bg-white/15"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
                title="Next"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default MakeCapacityListing;